// Compact relative-time formatter used for sidebar rows, search hits, and
// anywhere else we want a glanceable "how recent is this?" indicator.
//
// The output is deliberately terse (fits in 5-ish chars) so it can live on
// the right edge of a narrow row without pushing the title. For older items
// we fall back to an absolute "Mmm DD" because relative time loses meaning
// beyond a week.

// Firestore timestamps have a .toMillis() method; plain numbers/dates also
// work. Returns milliseconds since epoch, or null if the input is unusable.
export function tsToMs(ts) {
  if (ts == null) return null;
  if (typeof ts === 'number') return ts;
  if (ts instanceof Date) return ts.getTime();
  if (typeof ts.toMillis === 'function') return ts.toMillis();
  if (typeof ts.seconds === 'number') return ts.seconds * 1000;
  return null;
}

const MONTHS = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];

/**
 * formatRelative(ts, now=Date.now()) -> short string.
 *
 * Scale:
 *   < 45s        → "now"
 *   < 60m        → "Xm"
 *   < 24h        → "Xh"
 *   same yday    → "yday"
 *   < 7d         → "Xd"
 *   same year    → "Apr 12"
 *   older        → "Apr 12 '24"
 */
export function formatRelative(ts, now = Date.now()) {
  const ms = tsToMs(ts);
  if (ms == null) return '';
  const diff = now - ms;
  if (diff < 0) return 'now';

  const sec = Math.floor(diff / 1000);
  if (sec < 45) return 'now';

  const min = Math.floor(sec / 60);
  if (min < 60) return `${min}m`;

  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr}h`;

  // Day-level — switch to calendar-day comparison so "yday" is accurate
  // regardless of exact 24h offset.
  const d = new Date(ms);
  const n = new Date(now);
  const dayStart = (x) => new Date(x.getFullYear(), x.getMonth(), x.getDate()).getTime();
  const daysApart = Math.round((dayStart(n) - dayStart(d)) / (24 * 60 * 60 * 1000));

  if (daysApart === 1) return 'yday';
  if (daysApart < 7)   return `${daysApart}d`;

  const sameYear = d.getFullYear() === n.getFullYear();
  const mo = MONTHS[d.getMonth()];
  const day = d.getDate();
  if (sameYear) return `${mo} ${day}`;
  const yy = String(d.getFullYear()).slice(-2);
  return `${mo} ${day} '${yy}`;
}

/**
 * Long form for tooltips — "Apr 22, 2026, 3:45 PM". Good hover companion
 * to the terse formatRelative label.
 */
export function formatAbsolute(ts) {
  const ms = tsToMs(ts);
  if (ms == null) return '';
  try {
    return new Date(ms).toLocaleString(undefined, {
      year: 'numeric', month: 'short', day: 'numeric',
      hour: 'numeric', minute: '2-digit',
    });
  } catch {
    return new Date(ms).toString();
  }
}
